import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  ArrowLeft, 
  HelpCircle,
  Search,
  ChevronDown,
  ChevronUp,
  User,
  CreditCard,
  MapPin,
  Shield,
  Clock,
  Star,
  Phone,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = [
    { icon: User, title: "Account", description: "Profile & settings" },
    { icon: CreditCard, title: "Payments", description: "Fares & refunds" },
    { icon: MapPin, title: "Rides", description: "Booking & issues" },
    { icon: Shield, title: "Safety", description: "Emergency help" },
    { icon: Star, title: "Ratings", description: "Reviews & feedback" },
    { icon: Clock, title: "Scheduling", description: "Future rides" },
  ];

  const faqs = [
    {
      category: "Rides",
      question: "How do I book a ride?",
      answer: "Super easy: Open the app, type where you're going, and tap 'Request Ride'. You'll see available drivers nearby and what it'll cost. Confirm your pickup spot and hit 'Confirm'. A driver will be on their way in minutes."
    },
    {
      category: "Rides",
      question: "How do I cancel a ride?",
      answer: "Tap on your active ride and hit 'Cancel Ride'. If you cancel within 2 minutes of requesting, it's free. Once a driver's heading your way, there might be a small fee. Scheduled rides? Cancel up to an hour before, no charge."
    },
    {
      category: "Payments",
      question: "How is my fare calculated?",
      answer: "It's: base fare ($2.50) + distance ($0.90-$2.00/mile depending on demand) + time ($0.15/min) + service fee ($2.99). If it's busy, surge pricing might kick in—but we always show you the price before you confirm."
    },
    {
      category: "Payments",
      question: "How do I add a payment method?",
      answer: "Go to your profile, tap 'Payment Methods', then 'Add Payment Method'. You can add cards or link other payment services. Everything's securely handled through Stripe—we never see your full card number."
    },
    {
      category: "Payments",
      question: "How do I get a refund?",
      answer: "If something went wrong, go to your ride history, tap the ride, and hit 'Get Help'. Tell us what happened and we'll look into it within 24-48 hours. Refunds usually hit your account in 5-7 business days."
    },
    {
      category: "Account",
      question: "How do I update my info?",
      answer: "Tap your profile icon in the app. From there you can change your name, phone, email, or photo. Some changes need verification for security—just follow the prompts."
    },
    {
      category: "Account",
      question: "Forgot my password—help?",
      answer: "On the login screen, tap 'Forgot Password'. Enter your email and we'll send a reset link. It expires after 24 hours, so don't sleep on it."
    },
    {
      category: "Safety",
      question: "How does the SOS button work?",
      answer: "During any ride, tap the shield icon, then 'Emergency SOS'. This shares your location with emergency services and alerts our safety team. There's a 5-second countdown so you don't accidentally trigger it."
    },
    {
      category: "Safety",
      question: "How do I share my trip?",
      answer: "While you're riding, tap 'Share Trip'. You can send your real-time location via text, email, or any messaging app. Your friend gets a link to watch your ride until you arrive safely."
    },
    {
      category: "Safety",
      question: "How do I report a problem?",
      answer: "Go to your ride history, tap the trip, and select 'Report an Issue'. Pick what happened and give us details. Our team reviews safety concerns within 24 hours. For emergencies, always call 911 first."
    },
    {
      category: "Ratings",
      question: "How do ratings work?",
      answer: "After each ride, you and your driver rate each other 1-5 stars. Your rating is the average of your last 500 trips. Good ratings unlock perks; consistently low ratings might mean we need to chat."
    },
    {
      category: "Scheduling",
      question: "How far ahead can I schedule?",
      answer: "2 hours to 14 days in advance. Just tap 'Schedule' on the home screen, pick your date and time, add your destination, and confirm. We'll remind you before your pickup."
    },
    {
      category: "Scheduling",
      question: "What if no driver is available for my scheduled ride?",
      answer: "We'll heads-up you 30 minutes before if we're having trouble finding someone. You can wait it out, adjust your time, or cancel without a fee. Pro tip: Schedule important rides with extra buffer time."
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero with Search */}
      <section className="py-16 px-6 md:px-12 bg-gradient-to-b from-red-600/20 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <HelpCircle className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-['Barlow_Condensed'] font-black text-white uppercase mb-4">
            How Can We Help?
          </h1>
          <p className="text-slate-400 mb-8">
            Search for answers or browse the categories below. We've got you.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help..."
              className="pl-12 py-6 bg-slate-800 border-slate-700 text-white text-lg"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-8 text-center">
            Browse Topics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat, index) => (
              <button
                key={index}
                onClick={() => setSearchQuery(cat.title)}
                className="p-4 bg-slate-800/50 border border-slate-700 text-center hover:border-red-500/50 transition-colors"
              >
                <cat.icon className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <h3 className="text-white font-medium text-sm">{cat.title}</h3>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-8 text-center">
            Common Questions
          </h2>
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-2 py-1 bg-red-600/20 text-red-400 rounded">
                      {faq.category}
                    </span>
                    <span className="text-white font-medium">{faq.question}</span>
                  </div>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-4 pb-4">
                    <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
            {filteredFaqs.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                No results for "{searchQuery}". Try something else?
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-8 text-center">
            Still Stuck?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/contact" className="block">
              <div className="p-6 bg-slate-800/50 border border-slate-700 text-center hover:border-red-500/50 transition-colors h-full">
                <MessageSquare className="w-10 h-10 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Message Us</h3>
                <p className="text-slate-400 text-sm mb-4">
                  We'll get back to you within 24-48 hours
                </p>
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                  Send Message
                </Button>
              </div>
            </Link>
            <a href="tel:1-800-733-3466" className="block">
              <div className="p-6 bg-slate-800/50 border border-slate-700 text-center hover:border-red-500/50 transition-colors h-full">
                <Phone className="w-10 h-10 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Call Us</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Talk to a real human, 24/7
                </p>
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                  1-800-REDDI-NOW
                </Button>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Help;
